import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { GpuBudget, GpuBudgetCatalog } from '../../../../types/models/GpuBudget';
import { formatPrice } from '../../../../utils/money';
import { vatLabel } from '../gpuServerBilling';
import { catalogKind, formatQuantity, kindCopy } from './budgetLineKinds';
import { BUDGET_INCOMPLETE_TOTALS_NOTE } from './gpuBudgetCopy';

interface GpuBudgetTotalsCardProps {
  budget: GpuBudget;
  catalog: GpuBudgetCatalog;
}

/** "VAT (21 %)", or "VAT (reverse charge)" for a 0 % rate. */
export function budgetVatLabel(rate: string): string {
  return /^0+(\.0+)?$/.test(rate.trim()) ? 'VAT (reverse charge)' : `VAT (${vatLabel(rate)})`;
}

/**
 * The budget's monthly totals: one row per backend subtotal (always present, even at 0), then the
 * totals excl. VAT, VAT and incl. VAT. Every figure is the backend's string, formatted only — nothing
 * is added up here. A compact table with no search and no pagination (Bruno, 2026-09-29): one row
 * per line kind.
 */
const GpuBudgetTotalsCard: React.FunctionComponent<GpuBudgetTotalsCardProps> = ({ budget, catalog }) => (
  <Card>
    <CardTitle component="h3">Monthly total</CardTitle>
    <CardBody>
      <Table aria-label="Budget subtotals" variant="compact">
        <Thead>
          <Tr>
            <Th>Item</Th>
            <Th>Counted</Th>
            <Th modifier="nowrap">Monthly excl. VAT</Th>
            <Th modifier="nowrap">Monthly incl. VAT</Th>
          </Tr>
        </Thead>
        <Tbody>
          {budget.subtotals.map((subtotal) => {
            const unit = catalogKind(catalog, subtotal.kind)?.quantity.unit ?? null;
            return (
              <Tr key={subtotal.kind}>
                <Td dataLabel="Item">{kindCopy(subtotal.kind).title}</Td>
                <Td dataLabel="Counted">{`${formatQuantity(unit, subtotal.quantity)} counted`}</Td>
                <Td dataLabel="Monthly excl. VAT" modifier="nowrap">
                  {formatPrice(subtotal.monthly_ht)}
                </Td>
                <Td dataLabel="Monthly incl. VAT" modifier="nowrap">
                  {formatPrice(subtotal.monthly_ttc)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-md" aria-label="Budget totals">
        <DescriptionListGroup>
          <DescriptionListTerm>Total excl. VAT</DescriptionListTerm>
          <DescriptionListDescription>{formatPrice(budget.totals.monthly_ht)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>{budgetVatLabel(budget.vat_rate)}</DescriptionListTerm>
          <DescriptionListDescription>{formatPrice(budget.totals.monthly_vat)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>
            <strong>Total incl. VAT per month</strong>
          </DescriptionListTerm>
          <DescriptionListDescription>
            <strong>{formatPrice(budget.totals.monthly_ttc)}</strong>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {!budget.totals.complete && (
        <Content component="p" className="pf-v6-u-mt-md pf-v6-u-text-color-subtle">
          {BUDGET_INCOMPLETE_TOTALS_NOTE}
        </Content>
      )}
    </CardBody>
  </Card>
);

export default GpuBudgetTotalsCard;
